import { useMemo } from "react";

import { useI18n } from "~/i18n";
import type { OrgMember } from "../../orgMembers";
import { isSelfMember, resolveMemberPersonAvatar } from "./morrowMemberAvatarResolve";
import { MorrowPersonCard, personRoleLine } from "./MorrowPersonCard";

type TeamHierarchyViewProps = {
  readonly joinedMembers: ReadonlyArray<OrgMember>;
  readonly invitedMembers: ReadonlyArray<OrgMember>;
  readonly resolveAvatar?: ((key: string) => string | undefined) | undefined;
  readonly selfUserId?: string | null;
  readonly selfEmail?: string;
  readonly selfAvatarSeed?: string;
  readonly selfTitle?: string;
  readonly selfRole?: string;
  readonly onOpenMember: (member: OrgMember) => void;
};

/** A member's Clerk role marks them as org leadership (admin/owner). */
function isAdminRole(role: string): boolean {
  const normalized = role.trim().toLowerCase();
  return normalized.includes("admin") || normalized.includes("owner");
}

/**
 * The org roster grouped by the one structural signal our real directory carries:
 * the member's Clerk role. Leadership (admins/owners) reads first, then the rest
 * of the team, then anyone still pending an invite. We deliberately do NOT invent
 * departments or reporting lines — the server directory has neither, so grouping
 * stays on real data. `resolveAvatar` supplies each member's real Clerk photo when
 * one exists; otherwise the card falls back to initials.
 */
export function TeamHierarchyView({
  joinedMembers,
  invitedMembers,
  resolveAvatar,
  selfUserId = null,
  selfEmail = "",
  selfAvatarSeed = "",
  selfTitle = "",
  selfRole = "",
  onOpenMember,
}: TeamHierarchyViewProps) {
  const { t } = useI18n();

  const { admins, members } = useMemo(() => {
    const admins: OrgMember[] = [];
    const members: OrgMember[] = [];
    for (const member of joinedMembers) {
      (isAdminRole(member.clerkRole) ? admins : members).push(member);
    }
    return { admins, members };
  }, [joinedMembers]);

  return (
    <div className="@container flex flex-col gap-8">
      {admins.length > 0 ? (
        <HierarchySection
          title={t("team.roleAdmin")}
          members={admins}
          resolveAvatar={resolveAvatar}
          selfUserId={selfUserId}
          selfEmail={selfEmail}
          selfAvatarSeed={selfAvatarSeed}
          selfTitle={selfTitle}
          selfRole={selfRole}
          onOpenMember={onOpenMember}
        />
      ) : null}

      {members.length > 0 ? (
        <HierarchySection
          title={t("team.members")}
          members={members}
          resolveAvatar={resolveAvatar}
          selfUserId={selfUserId}
          selfEmail={selfEmail}
          selfAvatarSeed={selfAvatarSeed}
          selfTitle={selfTitle}
          selfRole={selfRole}
          onOpenMember={onOpenMember}
        />
      ) : null}

      {invitedMembers.length > 0 ? (
        <HierarchySection
          title={t("team.invited")}
          members={invitedMembers}
          pending
          resolveAvatar={resolveAvatar}
          selfUserId={selfUserId}
          selfEmail={selfEmail}
          selfAvatarSeed={selfAvatarSeed}
          selfTitle={selfTitle}
          selfRole={selfRole}
          onOpenMember={onOpenMember}
        />
      ) : null}
    </div>
  );
}

function HierarchySection({
  title,
  members,
  pending,
  resolveAvatar,
  selfUserId,
  selfEmail,
  selfAvatarSeed,
  selfTitle,
  selfRole,
  onOpenMember,
}: {
  readonly title: string;
  readonly members: ReadonlyArray<OrgMember>;
  readonly pending?: boolean | undefined;
  readonly resolveAvatar?: ((key: string) => string | undefined) | undefined;
  readonly selfUserId?: string | null;
  readonly selfEmail?: string;
  readonly selfAvatarSeed?: string;
  readonly selfTitle?: string;
  readonly selfRole?: string;
  readonly onOpenMember: (member: OrgMember) => void;
}) {
  return (
    <section className="flex min-w-0 flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground">
          {title}
        </h2>
        <span className="text-xs tabular-nums text-muted-foreground/70">({members.length})</span>
      </div>
      <ul className="grid grid-cols-1 gap-3 @[22rem]:grid-cols-2 @[40rem]:grid-cols-3 @[56rem]:grid-cols-4">
        {members.map((member) => (
          <li key={member.email} className="min-w-0">
            <HierarchyPersonTile
              member={member}
              onOpen={() => onOpenMember(member)}
              {...(pending ? { pending } : {})}
              {...(resolveAvatar ? { resolveAvatar } : {})}
              {...(selfUserId !== undefined ? { selfUserId } : {})}
              {...(selfEmail !== undefined ? { selfEmail } : {})}
              {...(selfAvatarSeed !== undefined ? { selfAvatarSeed } : {})}
              {...(selfTitle !== undefined ? { selfTitle } : {})}
              {...(selfRole !== undefined ? { selfRole } : {})}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

function HierarchyPersonTile({
  member,
  pending,
  resolveAvatar,
  selfUserId,
  selfEmail,
  selfAvatarSeed,
  selfTitle = "",
  selfRole = "",
  onOpen,
}: {
  readonly member: OrgMember;
  readonly pending?: boolean | undefined;
  readonly resolveAvatar?: ((key: string) => string | undefined) | undefined;
  readonly selfUserId?: string | null;
  readonly selfEmail?: string;
  readonly selfAvatarSeed?: string;
  readonly selfTitle?: string;
  readonly selfRole?: string;
  readonly onOpen: () => void;
}) {
  const { t } = useI18n();
  const name = member.name?.trim() || member.email;
  const isSelf = isSelfMember(member, selfUserId, selfEmail);
  const title = isSelf ? selfTitle.trim() || member.title : member.title;
  const role = isSelf ? selfRole.trim() || member.functionRole : member.functionRole;
  const description =
    member.description.trim() ||
    personRoleLine(title, role) ||
    (pending ? t("team.pendingInvite") : t("team.profilePending"));
  const { imageUrl, avatarSeed } = resolveMemberPersonAvatar({
    email: member.email,
    userId: member.userId,
    name,
    avatarSeed: member.avatarSeed,
    ...(resolveAvatar ? { resolveClerkPortrait: resolveAvatar } : {}),
    isSelf,
    ...(selfAvatarSeed ? { selfAvatarSeed } : {}),
  });

  return (
    <MorrowPersonCard
      {...(pending ? { pending: true } : {})}
      name={name}
      title={title}
      role={role}
      {...(imageUrl ? { imageUrl } : {})}
      {...(avatarSeed ? { avatarSeed } : {})}
      description={description}
      ariaLabel={t("team.openDetail", { name })}
      onClick={onOpen}
    />
  );
}
